import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronDown, Loader2, CheckCircle2, ArrowLeft, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { waLink, CONTACT, IMG_DIMS, IMG_ALTS, srcSetFor } from "@/lib/content";
import { SERVICES_DATA, SERVICE_SEO } from "@/lib/servicesData";
import { trackContactForm } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

const CREDIT_DISCLAIMER_SLUGS = ["credito-rural", "reestruturacao-de-dividas-rurais"];

const WA_ICON = (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 1 1 7.99 4.42zm8.02-17.45A11.27 11.27 0 0 0 12.05.75C5.8.75.72 5.83.72 12.08c0 2 .52 3.95 1.52 5.66L.62 23.62l6.02-1.58a11.3 11.3 0 0 0 5.41 1.38h.01c6.25 0 11.33-5.08 11.33-11.33 0-3.03-1.18-5.87-3.32-8.01z" />
  </svg>
);

const waMessage = (waMsg) => waLink(waMsg);

export const ServicePage = ({ slug }) => {
  const d = SERVICES_DATA[slug];
  const [open, setOpen] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!d) return;
    const seo = SERVICE_SEO[slug];
    document.title = seo.title;
    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    setMeta("name", "description", seo.desc);
    setMeta("property", "og:title", seo.title);
    return () => {
      document.title = "Melo Agro Engenharia | Crédito rural e laudos em Pelotas/RS";
    };
  }, [d, slug]);

  if (!d) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Envio indisponível. Fale conosco pelo WhatsApp.");
    if (!form.consent) return toast.error("É preciso aceitar a Política de Privacidade.");
    trackContactForm(window.location.pathname);
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service: d.title, email: "", website: e.currentTarget.website?.value || "" });
      setSent(true);
      setForm({ name: "", phone: "", message: "", consent: false });
      toast.success("Mensagem enviada! Retornaremos em breve.");
    } catch (err) {
      toast.error("Não foi possível enviar. Fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-2 w-full border-b border-[#1A2218]/20 bg-transparent py-2 text-[#1A2218] outline-none transition-colors duration-300 focus:border-[#5D995E]";

  return (
    <main data-testid={`service-page-${slug}`} className="pt-[72px]">
      <section className="grain relative overflow-hidden bg-[#1B2F1D] text-[#F9F8F3]">
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pb-16 pt-16 sm:px-8 md:pt-20 lg:grid-cols-12">
          <div className="relative z-10 lg:col-span-7">
            <Reveal><Overline light>Pelotas · Metade Sul do RS</Overline></Reveal>
            <h1 data-testid={`service-h1-${slug}`} className="mt-6 font-serif text-4xl font-semibold leading-[1.02] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
              {d.title}
            </h1>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[#F9F8F3]/80 md:text-lg">{d.intro}</p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid={`service-wa-cta-${slug}`}
                  href={waMessage(d.waMsg)}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#F9F8F3] px-7 py-4 text-sm font-semibold text-[#16220F] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8FC690]"
                >
                  {WA_ICON} Falar no WhatsApp
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </a>
                <Link
                  to="/"
                  data-testid={`service-back-home-${slug}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F9F8F3]/30 px-7 py-4 text-sm font-semibold transition-colors duration-300 hover:border-[#8FC690]"
                >
                  <ArrowLeft className="h-4 w-4" /> Voltar ao início
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1} className="overflow-hidden rounded-[24px] rounded-tl-[90px]">
              <img
                src={d.image}
                alt={IMG_ALTS[d.image] || `${d.title} — Melo Agro Engenharia`}
                srcSet={srcSetFor(d.image)}
                sizes="(min-width:1024px) 40vw, calc(100vw - 40px)"
                width={IMG_DIMS[d.image]?.[0] || 1600}
                height={IMG_DIMS[d.image]?.[1] || 1200}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 md:py-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:items-start lg:gap-10">
          <div className="lg:col-span-6">
            <Reveal><Overline>Para quem é</Overline></Reveal>
            <div className="mt-8 space-y-4">
              {d.audiences.map((a) => (
                <div key={a.t} data-testid={`service-audience-${slug}`} className="rounded-2xl border border-[#E5E0D5] bg-white p-5">
                  <p className="font-serif text-xl text-[#1A2218]">{a.t}</p>
                  <p className="mt-1 text-base leading-relaxed text-[#4A5549]">{a.d}</p>
                </div>
              ))}
            </div>
            <Reveal>
              <h2 className="mt-12 font-serif text-3xl text-[#1A2218] sm:text-4xl">Problemas que resolve</h2>
              <ul className="mt-6 space-y-3">
                {d.problems.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[#2C332A]/90">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-[#5D995E]" />
                    <span className="text-base leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal><Overline>Como funciona</Overline></Reveal>
            <div className="mt-8 space-y-6">
              {d.steps.map((st, i) => (
                <div key={st.t} data-testid={`service-step-${slug}-${i + 1}`} className="flex gap-5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#233D25] font-mono text-xs text-[#8FC690]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-serif text-xl text-[#1A2218]">{st.t}</p>
                    <p className="mt-1 text-base leading-relaxed text-[#4A5549]">{st.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <Reveal>
              <h2 className="mt-12 font-serif text-3xl text-[#1A2218] sm:text-4xl">Documentos necessários</h2>
              <ul className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {d.docs.map((doc) => (
                  <li key={doc} data-testid={`service-docs-${slug}`} className="flex items-start gap-2 rounded-xl bg-[#F2EFE9] px-4 py-3 text-sm text-[#2C332A]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#233D25]" /> {doc}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#F2EFE9] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-6">
            <Reveal><Overline>Perguntas frequentes</Overline></Reveal>
            <div className="mt-8" data-testid={`service-faq-${slug}`}>
              {d.faq.map((f, i) => (
                <div key={f.q} className="border-b border-[#E5E0D5]">
                  <button
                    data-testid={`service-faq-${slug}-q${i + 1}`}
                    onClick={() => setOpen(open === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-4 py-5 text-left"
                    aria-expanded={open === i}
                  >
                    <span className="font-medium text-[#1A2218]">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-[#5D995E] transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="pb-5 text-base leading-relaxed text-[#4A5549]">{f.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal>
              <div data-testid={`service-form-${slug}`} className="rounded-[28px] border border-[#E5E0D5] bg-white p-6 shadow-[0_30px_80px_-40px_rgba(31,58,36,0.35)] sm:p-8">
                <h2 className="font-serif text-3xl text-[#1A2218]">Fale com a equipe</h2>
                <p className="mt-2 text-sm text-[#4A5549]">Sobre {d.title.toLowerCase()} — respondemos rapidinho.</p>
                <form onSubmit={submit} className="mt-6 space-y-5">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                  <input
                    required
                    minLength={2}
                    data-testid={`service-form-name-${slug}`}
                    placeholder="Seu nome"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={inputCls}
                  />
                  <input
                    data-testid={`service-form-phone-${slug}`}
                    placeholder="Telefone / WhatsApp"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className={inputCls}
                  />
                  <textarea
                    required
                    minLength={5}
                    rows={3}
                    data-testid={`service-form-message-${slug}`}
                    placeholder="Conte brevemente sua necessidade"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className={`${inputCls} resize-none`}
                  />
                  <label className="flex items-start gap-3 text-sm text-[#4A5549]">
                    <input
                      type="checkbox"
                      required
                      checked={form.consent}
                      onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                      data-testid={`service-form-consent-${slug}`}
                      className="mt-0.5 h-4 w-4 accent-[#233D25]"
                    />
                    <span>
                      Autorizo o contato da Melo Agro Engenharia e li a{" "}
                      <Link to="/politica-de-privacidade" className="font-semibold text-[#233D25] underline underline-offset-2">Política de Privacidade</Link>. *
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={`service-form-submit-${slug}`}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#233D25] px-6 py-4 font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#3E5B3F] disabled:opacity-70"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : sent ? <CheckCircle2 className="h-5 w-5" /> : null}
                    {loading ? "Enviando..." : "Enviar mensagem"}
                  </button>
                  {sent && <p data-testid={`service-form-success-${slug}`} className="text-sm font-medium text-[#233D25]">Recebemos sua mensagem! Retornamos em até 1 dia útil pelo WhatsApp.</p>}
                </form>
                <p className="mt-5 text-center text-sm text-[#4A5549]">
                  ou ligue: <a href={waMessage(d.waMsg)} target="_blank" rel="noreferrer" className="font-semibold text-[#233D25]">{CONTACT.phone}</a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      {d.maquinasSection && (
        <section className="bg-[#F2EFE9] py-16 md:py-28" id="maquinas">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
            <Reveal><Overline>Avaliação de máquinas e implementos agrícolas</Overline></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-3xl text-base leading-relaxed text-[#2C332A] sm:text-lg">{d.maquinasSection.text}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {d.maquinasSection.items.map((it, i) => (
                <Reveal key={it.t} delay={0.05 * i}>
                  <div data-testid={`maquinas-item-${i + 1}`} className="h-full rounded-[20px] border border-[#E5E0D5] bg-white p-6">
                    <span className="font-mono text-xs tracking-[0.15em] text-[#5D995E]">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-3 font-serif text-xl leading-snug text-[#1A2218]">{it.t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#4A5549]">{it.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      {CREDIT_DISCLAIMER_SLUGS.includes(slug) && (
        <div className="mx-auto max-w-[900px] px-5 pb-14 sm:px-8">
          <p data-testid="service-credit-disclaimer" className="text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.14em] text-[#4A5549]">
            Não prometemos aprovação, renegociação ou prorrogação de crédito: a decisão é sempre da instituição financeira.
          </p>
        </div>
      )}
    </main>
  );
};
